import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'


import { BannerApp } from '@widgets/app-banner'

import SEO from '@shared/components/SEO'
import { BRAND_NAME } from '@shared/contants/app'
import { PAYMENTS } from '@shared/contants/payments'
import { cn } from '@shared/lib/cn'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Container from '@shared/ui/container'
import Wrapper from '@shared/ui/wrapper'

import styles from './styles.module.scss'

type TabType = 'payment' | 'delivery' | 'return'

export default function CustomerInfoPage () {
  const { t: tc } = useTranslation('customerInfo')
  const { t: ts } = useTranslation('seo')
  const [ activeTab, setActiveTab ] = useState<TabType>('payment')
  const [ openFaqItems, setOpenFaqItems ] = useState<Record<number, boolean>>({
    1: true,
  })
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  const toggleFaqItem = (num: number) => {
    setOpenFaqItems((prev) => ({
      ...prev,
      [num]: !prev[num],
    }))
  }

  const tabs: { value: TabType; label: string }[] = [
    { value: 'payment', label: tc('tabs.payment') },
    { value: 'delivery', label: tc('tabs.delivery') },
    { value: 'return', label: tc('tabs.return') },
  ]

  const activeTabIndex = tabs.findIndex((t) => t.value === activeTab)
  const indicatorWidth = tabRefs.current[activeTabIndex]?.offsetWidth || 0
  const indicatorLeft = tabRefs.current
    .slice(0, activeTabIndex)
    .reduce((sum, ref) => sum + (ref?.offsetWidth || 0) + 32, 0)

  return (
    <Container>
      <SEO
        title={ts('customerInfo.title', { brand: BRAND_NAME })}
        description={ts('customerInfo.description', { brand: BRAND_NAME })}
      />
      <Wrapper className={styles.wrapper}>
        <div className={styles.header}>
          <Breadcrumbs
            className={styles.breadcrumbs}
            links={[
              {
                title: tc('page'),
              },
            ]}
          />
          <h1 className={styles.title}>{tc('title')}</h1>
        </div>

        <div className={styles.tabs}>
          {tabs.map((tab, index) => (
            <button
              key={tab.value}
              ref={(el) => {
                tabRefs.current[index] = el
              }}
              type="button"
              className={`${styles.tabs__tab} ${
                activeTab === tab.value ? styles['tabs__tab--active'] : ''
              }`}
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.label}
            </button>
          ))}
          <div
            className={styles.tabs__indicator}
            style={{
              width: `${indicatorWidth}px`,
              transform: `translateX(${indicatorLeft}px)`,
            }}
          />
        </div>

        <div className={styles.content}>
          {activeTab === 'payment' && (
            <div className={styles.tabContent}>
              <p className={styles.section__description}>
                {tc('payment.description')}
              </p>
              
              <section className={styles.section}>
                <div className={styles.section__header}>
                  <img src="/icons/payments/card.svg" alt="" className={styles.section__icon} />
                  <h2 className={styles.section__title}>
                    {tc('payment.online.title')}
                  </h2>
                </div>
                <p className={styles.section__description}>
                  {tc('payment.online.description')}
                </p>
                <p className={styles.section__description}>
                  {tc('payment.online.cardsSupported')}
                </p>
                {PAYMENTS.items.length > 0 && (
                  <div className={styles.paymentMethods}>
                    {PAYMENTS.items.map((method) => (
                      <img
                        key={method.title}
                        src={method.icon}
                        alt={method.title}
                        className={styles.paymentMethods__icon}
                      />
                    ))}
                  </div>
                )}
                <p className={styles.section__subtitle}>
                  {tc('payment.online.howItWorks')}
                </p>
                <ol className={styles.stepsList}>
                  {(tc('payment.online.steps', { returnObjects: true }) as string[]).map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
                <p className={styles.section__description}>
                  {tc('payment.online.afterPayment')}
                </p>
                <p className={styles.section__description}>
                  {tc('payment.online.assemblyStatus')}
                </p>
                <p className={styles.section__description}>
                  {tc('payment.online.noChanges')}
                </p>
              </section>

              <section className={styles.section}>
                <div className={styles.section__header}>
                  <img src="/icons/payments/lock.svg" alt="" className={styles.section__icon} />
                  <h2 className={styles.section__title}>
                    {tc('payment.security.title')}
                  </h2>
                </div>
                <p className={styles.section__description}>
                  {tc('payment.security.description')}
                </p>
                <p className={styles.section__description}>
                  {tc('payment.security.noStorage')}
                </p>
              </section>
            </div>
          )}

          {activeTab === 'delivery' && (
            <div className={styles.tabContent}>
              <p className={styles.section__description}>
                {tc('delivery.description1')}
              </p>
              <p className={styles.section__description}>
                {tc('delivery.description2')}
              </p>

              <section className={styles.section}>
                <h2 className={styles.section__title}>
                  {tc('delivery.howItWorks.title')}
                </h2>
                <ol className={styles.stepsList}>
                  {(tc('delivery.howItWorks.steps', { returnObjects: true }) as string[]).map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              </section>

              <section className={styles.section}>
                <h2 className={styles.section__title}>
                  {tc('delivery.schedule.title')}
                </h2>
                <p className={styles.section__description}>
                  {tc('delivery.schedule.description1')}
                </p>
                <p className={styles.section__description}>
                  {tc('delivery.schedule.description2')}
                </p>
              </section>

              <section className={styles.section}>
                <h2 className={styles.section__title}>
                  {tc('delivery.cost.title')}
                </h2>
                <ul className={styles.bulletList}>
                  {(tc('delivery.cost.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className={styles.section}>
                <h2 className={styles.section__title}>
                  {tc('delivery.important.title')}
                </h2>
                <ul className={styles.bulletList}>
                  {(tc('delivery.important.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className={styles.section}>
                <h2 className={styles.section__title}>
                  {tc('delivery.whyUs.title')}
                </h2>
                <ul className={styles.bulletList}>
                  {(tc('delivery.whyUs.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>
            </div>
          )}

          {activeTab === 'return' && (
            <div className={styles.tabContent}>
              <section className={styles.section}>
                <h2 className={styles.section__title}>
                  {tc('return.refund.title')}
                </h2>
                <p className={styles.section__description}>
                  {tc('return.refund.description')}
                </p>
                <div className={styles.note}>
                  {tc('return.refund.note')}
                </div>
                <p className={styles.section__description}>
                  {tc('return.refund.process')}
                </p>
              </section>

              <section className={styles.section}>
                <h2 className={styles.section__title}>
                  {tc('return.faq.title')}
                </h2>
                <div className={styles.faq}>
                  {[ 1, 2, 3, 4, 5, 6, 7 ].map((num) => {
                    const isOpen = openFaqItems[num] || false
                    return (
                      <div
                        key={num}
                        className={cn(
                          styles.faqItem,
                          isOpen && styles.faqItemOpen
                        )}
                      >
                        <button
                          type="button"
                          className={styles.faqItem__header}
                          onClick={() => toggleFaqItem(num)}
                        >
                          <h3 className={styles.faqItem__question}>
                            {tc(`return.faq.q${num}`)}
                          </h3>
                          <div className={styles.faqItem__icon}>
                            <img
                              src={
                                isOpen
                                  ? '/icons/faq/chevron-up.svg'
                                  : '/icons/faq/chevron-down.svg'
                              }
                              alt=""
                            />
                          </div>
                        </button>
                        {isOpen && (
                          <div className={styles.faqItem__answer}>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: tc(`return.faq.a${num}`),
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </section>
            </div>
          )}
        </div>

        <div className={styles.block}>
          <BannerApp />
        </div>
      </Wrapper>
    </Container>
  )
}
